import React from 'react';
import { CalendarDays, ListChecks, UserRound } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import HubCard from './HubCard';
import SubpageHeader from './SubpageHeader';
import { useReserveContext } from '../../contexts/useReserveContext';
import { applySelectedStore } from './storeSelection';
import { fetchStoresForReserve } from '../../utils/reservation/by-Date/fetchStoresForReserve';
import { fetchStoresForServiceReserve } from '../../utils/reservation/by-service/fetchStoresForServiceReserve';
import { fetchStoresForStaffReserve } from '../../utils/reservation/by-staff/fetchStoresForStaffReserve';
import { useI18n } from '../../i18n';

const BookingHub: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useI18n();
  const [pendingFlow, setPendingFlow] = React.useState<'date' | 'service' | 'staff' | null>(null);
  const {
    setStoreId,
    setStoreName,
    setSelectedServices,
    setSelectedDate,
    setSelectedStaffId,
    setSelectedStaffName,
    setSelectedStartTime,
    setIsFreeReservation,
  } = useReserveContext();

  const storeSelectionActions = {
    setStoreId,
    setStoreName,
    setSelectedServices,
    setSelectedDate,
    setSelectedStartTime,
    setSelectedStaffId,
    setSelectedStaffName,
    setIsFreeReservation,
  };

  const handleStartFlow = async (
    flow: 'date' | 'service' | 'staff',
    options: {
      nextPath: string;
      storePath: string;
      fetchStores: () => Promise<Array<{ id: number; name: string }>>;
    },
  ) => {
    if (pendingFlow) return;

    setPendingFlow(flow);
    try {
      const stores = await options.fetchStores();

      if (stores.length === 1) {
        applySelectedStore(storeSelectionActions, stores[0]);
        navigate(options.nextPath);
        return;
      }

      navigate(options.storePath);
    } catch (error: any) {
      console.error(error);
      toast.error(error?.message || t('booking.loadStoresFailed'));
    } finally {
      setPendingFlow(null);
    }
  };

  return (
    <div className="space-y-4">
      <SubpageHeader
        title={t('booking.title')}
        onBack={() => navigate('/reserve')}
      />

      <div className="space-y-4">
        <HubCard
          title={t('booking.byStaff')}
          icon={UserRound}
          variant="glossy"
          onClick={() =>
            void handleStartFlow('staff', {
              nextPath: '/reserve/by-staff/staff',
              storePath: '/reserve/by-staff/store',
              fetchStores: fetchStoresForStaffReserve,
            })
          }
        />

        <HubCard
          title={t('booking.byDate')}
          icon={CalendarDays}
          variant="glossy"
          onClick={() =>
            void handleStartFlow('date', {
              nextPath: '/reserve/by-date/date',
              storePath: '/reserve/by-date/store',
              fetchStores: fetchStoresForReserve,
            })
          }
        />

        <HubCard
          title={t('booking.byService')}
          icon={ListChecks}
          variant="glossy"
          onClick={() =>
            void handleStartFlow('service', {
              nextPath: '/reserve/by-service/service',
              storePath: '/reserve/by-service/store',
              fetchStores: fetchStoresForServiceReserve,
            })
          }
        />
      </div>
    </div>
  );
};

export default BookingHub;
